// components/EnterpriseAIDilemma.tsx
import Image from 'next/image'
import CountUp from 'react-countup'

const stats = [
    { value: 30, suffix: '+', label: 'Models supported' },
    { value: 'Real-Time', label: 'Response speed' },
    { value: 0, label: 'Markup API cost' },
    { value: 100, suffix: '%', label: 'Data privacy' },
    { value: 99.99, suffix: '%', label: 'Uptime' },
]

export default function EnterpriseAIDilemma() {
    return (
        <section className='rounded-t-3xl py-20 px-6 bg-white pt-60 md:pt-32 xs:pt-60  pb-16 lg:px-12'>
            <div className='mx-auto flex max-w-[1248px] flex-col gap-20'>
                {/* Section 1 - Enterprise AI Dilemma */}
                <div className='grid items-start gap-12 lg:grid-cols-2'>
                    {/* Left Text */}
                    <div className='flex flex-col gap-4 text-center md:gap-6 md:text-left'>
                        <h2 className='bg-gradient-to-r from-cyan-600 to-blue-600 bg-clip-text text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold leading-snug text-transparent'>
                            The Enterprise AI Dilemma
                        </h2>
                        <p className='mx-auto max-w-2xl text-sm sm:text-base md:text-lg lg:text-xl font-medium text-[#7d7b7b] md:mx-0'>
                            Enterprises adopting AI often encounter serious challenges that limit
                            long-term success. Vendor lock-in restricts flexibility and innovation,
                            while sensitive data flowing through external servers raises major
                            privacy concerns.
                        </p>
                    </div>

                    {/* Right Cards */}
                    <div className='flex flex-col gap-2 '>
                        {/* Card 1 */}
                        <div className='flex flex-col gap-1'>
                            <div className='flex items-center gap-3'>
                                <div className='rounded-lg p-2.5'>
                                    <Image src='/Integra-ai/i1.png' alt='Vendor Lock-In' width={36} height={36} />
                                </div>
                                <h3 className='text-xl sm:text-2xl md:text-[28px] font-medium text-[#222]'>
                                    Vendor Lock-In
                                </h3>
                            </div>
                            <p className='mx-2 text-sm sm:text-base md:text-lg text-[#7d7b7b]'>
                                Trapped with a single AI model, limiting flexibility and innovation.
                            </p>
                        </div>

                        {/* Card 2 */}
                        <div className='flex flex-col gap-1'>
                            <div className='flex items-center gap-3'>
                                <div className='rounded-lg p-2.5'>
                                    <Image src='/Integra-ai/i2.png' alt='Data Privacy' width={36} height={36} />
                                </div>
                                <h3 className='text-xl sm:text-2xl md:text-[28px] font-medium text-[#222]'>
                                    Data Privacy Risks
                                </h3>
                            </div>
                            <p className='mx-2 text-sm sm:text-base md:text-lg text-[#7d7b7b]'>
                                Sensitive data is exposed to third-party servers with limited
                                control.
                            </p>
                        </div>

                        {/* Card 3 */}
                        <div className='flex flex-col gap-1'>
                            <div className='flex items-center gap-3'>
                                <div className='rounded-lg p-2.5'>
                                    <Image src='/Integra-ai/i3.png' alt='Cost Opacity' width={36} height={36} />
                                </div>
                                <h3 className='text-xl sm:text-2xl md:text-[28px] font-medium text-[#222]'>
                                    Cost Opacity & Lack of Control
                                </h3>
                            </div>
                            <p className='mx-2 text-sm sm:text-base md:text-lg text-[#7d7b7b]'>
                                No visibility into usage metrics or spending controls.
                            </p>
                        </div>
                    </div>
                </div>

                {/* Section 2 - IntegraAI Difference */}
                <div className='flex flex-col items-center gap-8 text-center'>
                    <div className='flex flex-col gap-3 pb-8 text-center md:text-left'>
                        <h2 className='bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text py-2 text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-inter font-bold leading-snug text-transparent'>
                            The IntegraAI Difference
                        </h2>
                        <p className='mx-auto mt-2 max-w-2xl text-sm sm:text-base md:text-lg lg:text-lg text-center font-semibold text-[#7d7b7b] md:mx-0'>
                            Multiple AI Models, One Conversation, Complete Control
                        </p>
                    </div>

                    {/* Feature Grid */}
                    <div className='mt-8 grid max-w-7xl grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4'>
                        {/* Card 1 */}
                        <div className='flex flex-col items-start text-start'>
                            <div className='flex items-center gap-3'>
                                <div className='rounded-lg p-2.5'>
                                    <Image src='/Integra-ai/s1.png' alt='Freedom' width={36} height={36} />
                                </div>
                                <h3 className='text-xl sm:text-2xl md:text-[28px] font-medium text-[#222]'>Freedom</h3>
                            </div>
                            <p className='mx-2 text-sm sm:text-base md:text-lg text-[#7d7b7b]'>
                                Use any AI model with your own API keys, eliminating vendor lock-in
                                and giving you ultimate flexibility.
                            </p>
                        </div>

                        {/* Card 2 */}
                        <div className='flex flex-col items-start text-start'>
                            <div className='flex items-center gap-3'>
                                <div className='rounded-lg p-2.5'>
                                    <Image src='/Integra-ai/s2.png' alt='Intelligence' width={36} height={36} />
                                </div>
                                <h3 className='text-xl sm:text-2xl md:text-[28px] font-medium text-[#222]'>Intelligence</h3>
                            </div>
                            <p className='mx-2 text-sm sm:text-base md:text-lg text-[#7d7b7b]'>
                                Query multiple AI models parallelly to compare responses and select
                                the best one for your needs.
                            </p>
                        </div>

                        {/* Card 3 */}
                        <div className='flex flex-col items-start text-start'>
                            <div className='flex items-center gap-3'>
                                <div className='rounded-lg p-2.5'>
                                    <Image src='/Integra-ai/s3.png' alt='Control' width={36} height={36} />
                                </div>
                                <h3 className='text-xl sm:text-2xl md:text-[28px] font-medium text-[#222]'>Control</h3>
                            </div>
                            <p className='mx-2 text-sm sm:text-base md:text-lg text-[#7d7b7b]'>
                                Enterprise-grade management with granular permissions, usage
                                tracking and budget controls.
                            </p>
                        </div>

                        {/* Card 4 */}
                        <div className='flex flex-col items-start text-start'>
                            <div className='flex items-center gap-3'>
                                <div className='rounded-lg p-2.5'>
                                    <Image src='/Integra-ai/s4.png' alt='Privacy' width={36} height={36} />
                                </div>
                                <h3 className='text-xl sm:text-2xl md:text-[28px] font-medium text-[#222]'>Privacy</h3>
                            </div>
                            <p className='mx-2 text-sm sm:text-base md:text-lg text-[#7d7b7b]'>
                                Self-hosted deployment options keep your sensitive data entirely
                                within your infrastructure.
                            </p>
                        </div>
                    </div>
                </div>
                 <div className='absolute z-20 w-full max-w-6xl px-8 -translate-y-1/2 left-1/2 -translate-x-1/2 top-0'>
                <div className='flex flex-wrap items-center justify-center gap-6 rounded-2xl border border-gray-200 bg-white p-4 shadow sm:gap-10 sm:p-5'>
                    {stats.map((stat, i) => (
                        <div key={i} className='w-34 flex flex-col items-center gap-4'>
                            <div className='bg-gradient-to-r from-purple-300 to-pink-300 bg-clip-text text-2xl font-inter font-bold text-transparent sm:text-4xl md:text-[44px]'>
                                {typeof stat.value === 'number' ?
                                    <CountUp
                                        start={100} 
                                        end={stat.value} 
                                        duration={2}
                                        decimals={stat.value % 1 !== 0 ? 2 : 0}
                                        suffix={stat.suffix || ''}
                                        enableScrollSpy
                                        scrollSpyDelay={200}
                                    />
                                :   <span>{stat.value}</span>}
                            </div>
                            <div className='text-center max-w-[130px] text-[10px] font-semibold uppercase text-gray-700 sm:text-xs md:text-sm'>
                                {stat.label}
                            </div>
                        </div>
                    ))}
                </div>
            </div>
            </div>
        </section>
    )
}
